import React, { useMemo, useState } from 'react';
import {
  getPromotionTargetingGroupLabel,
  matchesPromotionTargetingGroup,
  PROMOTION_TARGETING_GROUP_SEARCH_THRESHOLD,
  type PromotionTargetingGroup,
} from '../../utils/promotion-targeting-groups';

interface PromotionTargetingGroupsProps {
  groups: PromotionTargetingGroup[];
}

export function PromotionTargetingGroups({ groups }: PromotionTargetingGroupsProps): React.ReactElement | null {
  const [query, setQuery] = useState('');
  const showSearch = groups.length > PROMOTION_TARGETING_GROUP_SEARCH_THRESHOLD;

  const filteredGroups = useMemo(
    () =>
      showSearch
        ? groups.filter((group) => matchesPromotionTargetingGroup(group, query))
        : groups,
    [groups, query, showSearch],
  );

  if (groups.length === 0) {
    return null;
  }

  return (
    <div className="promotion-targeting-groups">
      <div className="promotion-targeting-groups-header">
        <span className="promotion-targeting-groups-label">Customer Groups</span>
        <span className="promotion-targeting-groups-count">
          {filteredGroups.length === groups.length
            ? `${groups.length}`
            : `${filteredGroups.length} of ${groups.length}`}
        </span>
      </div>
      {showSearch ? (
        <div className="search-wrap promotion-targeting-groups-search">
          <span className="search-icon" aria-hidden="true">
            🔍
          </span>
          <input
            type="search"
            className="search-input"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Search customer groups"
            aria-label="Search customer groups"
          />
        </div>
      ) : null}
      {filteredGroups.length === 0 ? (
        <p className="promotion-targeting-groups-empty">No matching customer groups</p>
      ) : (
        <ul className="promotion-targeting-groups-list">
          {filteredGroups.map((group, index) => {
            const label = getPromotionTargetingGroupLabel(group);
            const key = group.id !== undefined ? `id-${group.id}` : `name-${label || index}`;

            return (
              <li key={key} className="promotion-targeting-groups-item">
                {label}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
